import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Your Privacy Matters</CardTitle>
            <CardDescription>
              GDPR and CCPA compliant privacy policy explaining how we collect, use, and protect your data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <section>
              <h3 className="text-lg font-semibold mb-2">1. Information We Collect</h3>
              
              <h4 className="font-semibold mt-4 mb-2">Account Information</h4>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Name and email address</li>
                <li>Profile picture (optional)</li>
                <li>Account credentials (encrypted)</li>
              </ul>

              <h4 className="font-semibold mt-4 mb-2">Meeting Data</h4>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Meeting details (title, description, schedule)</li>
                <li>Chat messages</li>
                <li>Participant information</li>
                <li>Audio/video content when recording is enabled</li>
                <li>Transcriptions when enabled</li>
              </ul>

              <h4 className="font-semibold mt-4 mb-2">Usage Information</h4>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Device information (browser, OS, device type)</li>
                <li>IP address and location data</li>
                <li>Usage patterns and feature interactions</li>
                <li>Performance and error data</li>
              </ul>

              <h4 className="font-semibold mt-4 mb-2">Cookies and Tracking</h4>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Essential cookies for authentication</li>
                <li>Analytics cookies (with consent)</li>
                <li>Preference cookies for user settings</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">2. How We Use Your Information</h3>
              <p className="text-muted-foreground mb-2">We use collected information for:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Service Delivery:</strong> Provide video conferencing, chat, and collaboration features</li>
                <li><strong>Account Management:</strong> Create and maintain your account, authenticate users</li>
                <li><strong>Communication:</strong> Send service notifications, updates, and support messages</li>
                <li><strong>Improvement:</strong> Analyze usage to improve features and performance</li>
                <li><strong>Security:</strong> Detect fraud, abuse, and security threats</li>
                <li><strong>Legal Compliance:</strong> Comply with laws, regulations, and legal requests</li>
                <li><strong>Analytics:</strong> Understand how users interact with our service (anonymized when possible)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">3. Legal Basis for Processing (GDPR)</h3>
              <p className="text-muted-foreground mb-2">For EU users, we process your data based on:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Contract:</strong> Processing necessary to provide the service you requested</li>
                <li><strong>Consent:</strong> You have given explicit consent (e.g., marketing emails, cookies)</li>
                <li><strong>Legitimate Interest:</strong> Fraud prevention, security, service improvement</li>
                <li><strong>Legal Obligation:</strong> Compliance with legal requirements</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">4. Data Sharing and Disclosure</h3>
              <p className="text-muted-foreground mb-2">We may share your information with:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Service Providers:</strong> Cloud hosting (Supabase), analytics (Sentry), email services</li>
                <li><strong>Meeting Participants:</strong> Your name, profile info visible to other meeting participants</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect rights and safety</li>
                <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                We do NOT sell your personal data to third parties.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">5. Data Retention</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Account Data:</strong> Retained while your account is active</li>
                <li><strong>Meeting Data:</strong> Kept for 30 days after meeting ends (unless deleted earlier)</li>
                <li><strong>Chat Messages:</strong> Stored for 90 days</li>
                <li><strong>Recordings:</strong> Kept until manually deleted by host or for 1 year</li>
                <li><strong>Analytics:</strong> Aggregated data kept indefinitely, personal data deleted after 90 days</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                You can request deletion of your data at any time by contacting support.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">6. Your Rights (GDPR & CCPA)</h3>
              <p className="text-muted-foreground mb-2">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Object to certain types of processing</li>
                <li><strong>Withdraw Consent:</strong> Revoke consent for processing based on consent</li>
                <li><strong>Opt-Out:</strong> Opt out of marketing communications and cookies</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                To exercise these rights, contact us at support@meethub.com
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">7. Data Security</h3>
              <p className="text-muted-foreground mb-2">We implement security measures including:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>End-to-end encryption for video/audio streams</li>
                <li>TLS/SSL encryption for data in transit</li>
                <li>Encryption at rest for stored data</li>
                <li>Regular security audits and penetration testing</li>
                <li>Access controls and authentication</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">8. International Data Transfers</h3>
              <p className="text-muted-foreground">
                Your data may be transferred to and processed in countries outside your residence. We ensure adequate protection through standard contractual clauses and other safeguards compliant with GDPR.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">9. Children's Privacy</h3>
              <p className="text-muted-foreground">
                Our Service is not intended for users under 16 years old. We do not knowingly collect information from children. If you believe we have collected data from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">10. Cookie Policy</h3>
              <p className="text-muted-foreground mb-2">We use the following types of cookies:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Essential:</strong> Required for authentication and core functionality (cannot be disabled)</li>
                <li><strong>Analytics:</strong> Help us understand usage patterns (can be opted out)</li>
                <li><strong>Preferences:</strong> Remember your settings and preferences</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                You can manage cookie preferences through our cookie consent banner.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">11. Third-Party Services</h3>
              <p className="text-muted-foreground mb-2">We integrate with:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Supabase (database and authentication)</li>
                <li>Sentry (error tracking)</li>
                <li>Cloud storage providers</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                Each service has its own privacy policy governing their data practices.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">12. Changes to Privacy Policy</h3>
              <p className="text-muted-foreground">
                We may update this Privacy Policy periodically. We will notify you of significant changes via email or in-app notification. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-2">13. Contact and Data Protection Officer</h3>
              <p className="text-muted-foreground mb-2">
                For privacy concerns, data requests, or to exercise your rights:
              </p>
              <ul className="list-none pl-0 text-muted-foreground">
                <li>Company: MeetHub by TechTrustITSolutions</li>
                <li>Email: support@meethub.com</li>
                <li>Phone: +91-82009-10548</li>
                <li>Website: https://www.techtrustitsolutions.com/</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                EU users can also lodge complaints with their local supervisory authority.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
